import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MapPin, Phone, Clock, Mail, Navigation, 
  ChevronLeft, ChevronRight 
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function LocationsScreen() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);

  useEffect(() => {
    const fetchImages = async () => {
      setImagesLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/api/images/all`, {
          headers: {
            'Accept': 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
          },
        });
        const allImages = response.data.images || [];
        const locationImages = allImages
          .filter(img =>
            img.category === 'serviceCenter' &&
            img.isActive &&
            [10, 11, 12].includes(img.order)
          )
          .sort((a, b) => a.order - b.order)
          .map(img => img.imageUrl);
        setGalleryImages(locationImages);
      } catch (err) {
        console.error('Failed to load location images:', err);
      } finally {
        setImagesLoading(false);
      }
    };
    fetchImages();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || galleryImages.length === 0) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, galleryImages.length]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [galleryImages]);

  const handleManualInteraction = () => setIsAutoPlaying(false);

  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollProgress(height > 0 ? (winScroll / height) * 100 : 0);
    };
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const headquarters = {
    name: "ST Motors Headquarters",
    type: "Main Office & Showroom",
    address: "Thimphu, Bhutan",
    phone: "+975 17 24 24 82",
    email: "motors@sangaytsheltrim.com",
    description: "Our flagship location in Thimphu featuring a range of electric vehicles, test drive opportunities, expert consultation, and dedicated customer support.",
    mapUrl: "https://maps.app.goo.gl/25KEgXMcovGwij6o9",
  };

  const nextImage = () => {
    handleManualInteraction();
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    handleManualInteraction();
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const selectImage = (index) => {
    handleManualInteraction();
    setCurrentImageIndex(index);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)',
      color: '#E8E8E8',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Progress Bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '3px',
        background: '#C7B381',
        zIndex: 1000,
        transition: 'width 0.15s ease-out'
      }} />

      <div style={{
        padding: 'clamp(5rem, 10vw, 10rem) clamp(1.5rem, 5vw, 4rem)',
        maxWidth: '1440px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>

        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: 'clamp(4rem, 10vw, 7rem)' }}>
          <div style={{
            color: '#C7B381',
            fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            fontWeight: 500,
            marginBottom: '1.5rem'
          }}>
            Visit Us
          </div>
          <h1 style={{
            fontSize: 'clamp(3rem, 8vw, 6rem)',
            fontWeight: 200,
            margin: '0 0 1.5rem',
            letterSpacing: '-0.02em',
            lineHeight: 1.05
          }}>
            Our Location
          </h1>
          <div style={{
            width: '90px',
            height: '1px',
            background: '#C7B381',
            margin: '2rem auto'
          }} />
          <p style={{
            fontSize: 'clamp(1.1rem, 2.2vw, 1.35rem)',
            color: '#C0C0C0',
            maxWidth: '720px',
            margin: '0 auto',
            lineHeight: 1.7,
            fontWeight: 300
          }}>
            Visit our headquarters/showroom in Thimphu for electric vehicle consultations, test drives, and premium service.
          </p>
        </header>

        {/* Image Gallery */}
        {imagesLoading ? (
          <div style={{
            marginBottom: 'clamp(4rem, 8vw, 6rem)',
            borderRadius: '8px',
            border: '1px solid rgba(199,179,129,0.18)',
            height: isMobile ? '340px' : '580px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(199,179,129,0.04)',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(199,179,129,0.2)',
              borderTop: '3px solid #C7B381',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ color: '#C7B381', fontSize: '0.95rem' }}>Loading images...</span>
          </div>

        ) : galleryImages.length > 0 ? (
          <>
            {/* Main Slide */}
            <div style={{
              marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
              position: 'relative',
              borderRadius: '8px',
              overflow: 'hidden',
              background: 'rgba(199,179,129,0.04)',
              border: '1px solid rgba(199,179,129,0.18)',
              height: isMobile ? '340px' : '580px'
            }}>
              <img
                src={galleryImages[currentImageIndex]}
                alt={`ST Motors Showroom ${currentImageIndex + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  transition: 'opacity 0.6s ease'
                }}
              />

              {galleryImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    style={{
                      position: 'absolute',
                      left: 'clamp(1rem, 3vw, 2.5rem)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.55)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(199,179,129,0.35)',
                      borderRadius: '50%',
                      width: 'clamp(48px, 9vw, 56px)',
                      height: 'clamp(48px, 9vw, 56px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#C7B381',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(199,179,129,0.3)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.55)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                    }}
                  >
                    <ChevronLeft size={28} />
                  </button>

                  <button
                    onClick={nextImage}
                    style={{
                      position: 'absolute',
                      right: 'clamp(1rem, 3vw, 2.5rem)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.55)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(199,179,129,0.35)',
                      borderRadius: '50%',
                      width: 'clamp(48px, 9vw, 56px)',
                      height: 'clamp(48px, 9vw, 56px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#C7B381',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(199,179,129,0.3)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.55)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                    }}
                  >
                    <ChevronRight size={28} />
                  </button>
                </>
              )}

              <div style={{
                position: 'absolute',
                bottom: '1.5rem',
                right: '1.5rem',
                background: 'rgba(0,0,0,0.65)',
                backdropFilter: 'blur(12px)',
                padding: '0.6rem 1.3rem',
                borderRadius: '6px',
                color: '#C7B381',
                fontSize: 'clamp(0.9rem, 1.6vw, 1rem)',
                fontWeight: 500
              }}>
                {currentImageIndex + 1} / {galleryImages.length}
              </div>
            </div>

            {/* Thumbnails */}
            {galleryImages.length > 1 && (
              <div style={{
                display: 'flex',
                gap: 'clamp(0.6rem, 1.2vw, 1.2rem)',
                marginBottom: 'clamp(4rem, 8vw, 6rem)',
                overflowX: 'auto',
                padding: '1rem 0',
                scrollBehavior: 'smooth'
              }}>
                {galleryImages.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => selectImage(idx)}
                    style={{
                      flex: '0 0 auto',
                      width: isMobile ? '90px' : '140px',
                      height: isMobile ? '68px' : '105px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: idx === currentImageIndex
                        ? '2.5px solid #C7B381'
                        : '2px solid rgba(199,179,129,0.25)',
                      transition: 'all 0.25s ease',
                      opacity: idx === currentImageIndex ? 1 : 0.65,
                      transform: idx === currentImageIndex ? 'scale(1.04)' : 'scale(1)'
                    }}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {galleryImages.length === 1 && (
              <div style={{ marginBottom: 'clamp(4rem, 8vw, 6rem)' }} />
            )}
          </>

        ) : (
          <div style={{
            marginBottom: 'clamp(4rem, 8vw, 6rem)',
            borderRadius: '8px',
            border: '1px solid rgba(199,179,129,0.18)',
            height: isMobile ? '200px' : '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(199,179,129,0.04)',
            color: '#808080',
            fontSize: '1rem'
          }}>
            No showroom images available
          </div>
        )}

        {/* Details + Map */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 'clamp(2.5rem, 5vw, 4rem)',
          marginBottom: 'clamp(5rem, 10vw, 8rem)'
        }}>

          {/* Info Card */}
          <div style={{
            background: 'rgba(199,179,129,0.04)',
            border: '1px solid rgba(199,179,129,0.2)',
            borderRadius: '8px',
            padding: 'clamp(2.2rem, 5vw, 3.2rem)',
            height: 'fit-content',
            position: isMobile ? 'static' : 'sticky',
            top: '2rem'
          }}>
            <div style={{
              display: 'inline-block',
              padding: '0.6rem 1.4rem',
              background: 'rgba(199,179,129,0.18)',
              borderRadius: '6px',
              fontSize: 'clamp(0.8rem, 1.4vw, 0.9rem)',
              color: '#C7B381',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontWeight: 500,
              marginBottom: '1.8rem'
            }}>
              {headquarters.type}
            </div>

            <h2 style={{
              fontSize: 'clamp(1.8rem, 4vw, 2.4rem)',
              fontWeight: 300,
              marginBottom: '2.2rem',
              lineHeight: 1.25
            }}>
              {headquarters.name}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.8rem' }}>
              {[
                { icon: MapPin, label: 'Address', value: headquarters.address },
                { icon: Phone,  label: 'Contact', value: headquarters.phone },
                { icon: Mail,   label: 'Email',   value: headquarters.email },
                {
                  icon: Clock,
                  label: 'Hours',
                  value: (
                    <>
                      Monday - Friday: 9:00 AM - 5:00 PM<br />
                      Saturday: 8:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </>
                  )
                }
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start' }}>
                  <item.icon size={24} style={{ color: '#C7B381', marginTop: '0.3rem', flexShrink: 0 }} />
                  <div>
                    <div style={{
                      fontSize: 'clamp(0.8rem, 1.4vw, 0.9rem)',
                      color: '#999',
                      marginBottom: '0.5rem',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase'
                    }}>
                      {item.label}
                    </div>
                    <div style={{
                      fontSize: 'clamp(1rem, 1.9vw, 1.15rem)',
                      lineHeight: 1.5,
                      fontWeight: 300
                    }}>
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p style={{
              margin: '2.5rem 0',
              fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
              lineHeight: 1.8,
              color: '#B8B8B8',
              fontWeight: 300,
              borderTop: '1px solid rgba(199,179,129,0.12)',
              paddingTop: '2rem'
            }}>
              {headquarters.description}
            </p>

            <a
              href={headquarters.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.9rem',
                padding: '1rem 2.2rem',
                background: 'rgba(199,179,129,0.18)',
                border: '1px solid rgba(199,179,129,0.35)',
                borderRadius: '6px',
                color: '#C7B381',
                fontSize: 'clamp(0.95rem, 1.7vw, 1.05rem)',
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(199,179,129,0.28)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(199,179,129,0.18)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Navigation size={20} />
              Get Directions
            </a>
          </div>

          {/* Map */}
          <div style={{
            background: 'rgba(199,179,129,0.04)',
            border: '1px solid rgba(199,179,129,0.2)',
            borderRadius: '8px',
            overflow: 'hidden',
            height: isMobile ? '420px' : '640px',
            position: 'relative'
          }}>
            <iframe
              src="https://maps.google.com/maps?q=27.43296,89.64997&hl=en&z=19&output=embed"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                filter: 'grayscale(0.25) contrast(1.15) saturate(0.9)'
              }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="ST Motors Headquarters - Thimphu, Bhutan"
            />
          </div>
        </div>

        {/* What to Expect */}
        <div style={{
          background: 'rgba(199,179,129,0.04)',
          border: '1px solid rgba(199,179,129,0.2)',
          borderRadius: '8px',
          padding: 'clamp(2.5rem, 6vw, 4rem)',
          marginBottom: 'clamp(5rem, 10vw, 8rem)'
        }}>
          <h3 style={{
            fontSize: 'clamp(1.4rem, 2.8vw, 1.8rem)',
            color: '#C7B381',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
            marginBottom: '2.5rem'
          }}>
            What to Expect
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 'clamp(2rem, 4vw, 3rem)'
          }}>
            {[
              { title: "Electric Vehicle Showroom", text: "Full range of EVs available for viewing, with expert advice and scheduled test drives." },
              { title: "Service & Charging",        text: "Professional maintenance, repairs, and EV charging facilities by trained technicians." },
              { title: "Customer Experience",       text: "Friendly team to help with inquiries, financing, offers, and after-sales support." }
            ].map((item, i) => (
              <div key={i}>
                <div style={{
                  fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
                  fontWeight: 400,
                  color: '#E8E8E8',
                  marginBottom: '1rem'
                }}>
                  {item.title}
                </div>
                <p style={{
                  fontSize: 'clamp(0.95rem, 1.7vw, 1.05rem)',
                  color: '#B8B8B8',
                  lineHeight: 1.75,
                  fontWeight: 300
                }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer style={{
          textAlign: 'center',
          padding: 'clamp(4rem, 8vw, 6rem) 0 3rem',
          borderTop: '1px solid rgba(199,179,129,0.18)'
        }}>
          <div style={{
            width: '100px',
            height: '1px',
            background: '#C7B381',
            margin: '0 auto 2rem'
          }} />
          <p style={{
            fontSize: 'clamp(1.1rem, 2.2vw, 1.3rem)',
            color: '#C0C0C0',
            maxWidth: '680px',
            margin: '0 auto 1.5rem',
            lineHeight: 1.8
          }}>
            We look forward to welcoming you at our Thimphu showroom.
          </p>
          <div style={{
            fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)',
            color: '#666',
            letterSpacing: '0.15em',
            fontWeight: 500
          }}>
            ST Motors
          </div>
        </footer>

      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default LocationsScreen;